import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Card, CardText, Divider } from 'react-md';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Link } from 'react-router-dom';
import LangSwitch from './LangSwitch';
import ErrorDisplay from '../ErrorDisplay';
import FieldGroup from '../FieldGroup';
import LoaderButton from '../LoaderButton';

import { forgotPassword, confirmPassword, authenticate } from '../../libs/awsLib';

import './Login.css';

function parseInvalidPasswordException(e) {
  if (e.message.indexOf('Password not long') !== -1) return 'InvalidPasswordExceptionLong';
  if (e.message.indexOf('Password must have upper') !== -1) return 'InvalidPasswordExceptionUpper';
  if (e.message.indexOf('Password must have lower') !== -1) return 'InvalidPasswordExceptionLower';
  if (e.message.indexOf('Password must have numer') !== -1) return 'InvalidPasswordExceptionNumeric';
  if (e.message.indexOf('Password must have symbol') !== -1) return 'InvalidPasswordExceptionSymbol';
  return e.message;
}

function parseInvalidParameterException(e) {
  if (e.message.indexOf('Value at \'password\' failed') !== -1) return 'InvalidParameterExceptionPassword';
  return e.message;
}

export default injectIntl(class Signup extends Component {
  static propTypes = {
    intl: intlShape.isRequired,
    userHasAuthenticated: PropTypes.func.isRequired,
    handleChangeLocale: PropTypes.func.isRequired,
    history: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      email: '',
      password: '',
      confirmPassword: '',
      confirmationCode: '',
      newUser: null,
      error: '',
    };
  }

  validateForm() {
    return (
      this.state.email.length > 0
    );
  }

  validateConfirmationForm() {
    return this.state.confirmationCode.length > 0 &&
      this.state.password.length > 0 &&
      this.state.password === this.state.confirmPassword;
  }

  handleChange = (event) => {
    this.setState({
      [event.target.id]: event.target.value.trim(),
      error: undefined,
    });
  };

  handleSubmit = async (event) => {
    event.preventDefault();

    this.setState({ isLoading: true });

    try {
      const newUser = await forgotPassword(this.state.email);
      this.setState({
        newUser: newUser.user,
      });
    } catch (e) {
      this.setState({ isLoading: false });
      if (e.name === 'UsernameExistsException' || e.name === 'UserNotFoundException') {
        this.setState({ error: e.name });
      } else if (e.name === 'InvalidPasswordException') {
        this.setState({ error: parseInvalidPasswordException(e) });
      } else if (e.name === 'InvalidParameterException') {
        this.setState({ error: parseInvalidParameterException(e) });
      } else {
        console.log(e);
        alert(e.message);
      }
    }

    this.setState({ isLoading: false });
  };

  handleConfirmationSubmit = async (event) => {
    event.preventDefault();

    this.setState({ isLoading: true });

    try {
      await confirmPassword(this.state.newUser, this.state.confirmationCode, this.state.password);
      await authenticate(
        this.state.newUser,
        this.state.email,
        this.state.password
      );

      this.props.userHasAuthenticated(true);
      this.props.history.push('/');
    } catch (e) {
      alert(e);
      this.setState({ isLoading: false });
    }
  };

  renderConfirmationForm() {
    const { intl } = this.props;
    const { error, email } = this.state;
    return (
      <Card>
        <CardText className="welcome">
          <FormattedMessage id="login.definePassword" tagName="h3" />
        </CardText>
        <Divider />
        <CardText>
          <FormattedMessage id="forgot.mailsent" tagName="h3" />
          <FormattedMessage
            id="forgot.anEmailHasBeenSent"
            values={{ email: <b>{email}</b> }}
            defaultMessage="An email has been sent to {email} with instructions to reset your password."
          />
          <br /><br />

          <form onSubmit={this.handleConfirmationSubmit}>
            <ErrorDisplay error={error} />
            <FieldGroup
              bsSize="large"
              id="confirmationCode"
              autoFocus
              placeholder={intl.formatMessage({ id: 'signup.confirmationCodePlaceholder' })}
              type="tel"
              value={this.state.confirmationCode}
              onChange={this.handleChange}
              label={intl.formatMessage({ id: 'signup.confirmationCodeLabel' })}
              help={intl.formatMessage({ id: 'signup.confirmationCodeHelp' })}
            />

            <div className="passwordInstructions">
              <FormattedMessage id="login.instructions.title" />
              <ul>
                <li>8 <FormattedMessage id="login.instructions.characters" /></li>
                <li>1 <FormattedMessage id="login.instructions.uppercase" /></li>
                <li>1 <FormattedMessage id="login.instructions.number" /></li>
                <li>1 <FormattedMessage id="login.instructions.symbol" />{' (# @ & ! ? $ { } / \\  * - … )'}</li>
              </ul>
            </div>
            <FieldGroup
              id="password"
              label={intl.formatMessage({ id: 'signup.passwordLabel' })}
              labelClassName="md-full-width"
              bsSize="large"
              placeholder={intl.formatMessage({ id: 'signup.passwordPlaceholder' })}
              value={this.state.password}
              onChange={this.handleChange}
              type="password"
            />

            <FieldGroup
              id="confirmPassword"
              label={intl.formatMessage({ id: 'signup.confirmPasswordLabel' })}
              labelClassName="md-full-width"
              bsSize="large"
              placeholder={intl.formatMessage({ id: 'signup.confirmPasswordPlaceholder' })}
              value={this.state.confirmPassword}
              onChange={this.handleChange}
              type="password"
            />

            <LoaderButton
              flat
              swapTheming
              primary
              className="md-full-width big last"
              disabled={!this.validateConfirmationForm()}
              type="submit"
              isLoading={this.state.isLoading}
              text={intl.formatMessage({ id: 'login.definePasswordButton' })}
              loadingText={intl.formatMessage({ id: 'login.definePasswordButtonBussy' })}
            />
          </form>
        </CardText>
      </Card>
    );
  }
  renderEmailForm() {
    const { intl } = this.props;
    const { error } = this.state;
    return (
      <form onSubmit={this.handleSubmit}>
        <ErrorDisplay error={error} />

        <FieldGroup
          bsSize="large"
          id="email"
          autoFocus
          placeholder={intl.formatMessage({ id: 'signup.emailPlaceholder' })}
          type="email"
          value={this.state.email}
          onChange={this.handleChange}
          label={intl.formatMessage({ id: 'signup.emailLabel' })}
        />

        <LoaderButton
          flat
          swapTheming
          primary
          className="md-full-width big last"
          disabled={!this.validateForm()}
          type="submit"
          isLoading={this.state.isLoading}
          text={intl.formatMessage({ id: 'signup.registerButton' })}
          loadingText={intl.formatMessage({ id: 'signup.registerBussy' })}
        />
      </form>);
  }

  renderForm() {
    return (
      <Card>
        <CardText className="welcome">
          <FormattedMessage id="forgot.reinitPassword" tagName="h3" />
        </CardText>
        <Divider />
        <CardText>
          {this.renderEmailForm()}
        </CardText>
      </Card>
    );
  }

  render() {
    const { handleChangeLocale } = this.props;
    const { newUser } = this.state;
    return (
      <div>
        <LangSwitch handleChangeLocale={handleChangeLocale} />
        <div className="md-block-centered login">
          <div className="lander">
            <div className="logo md-block-centered" />
            {newUser === null
            ? this.renderForm()
            : this.renderConfirmationForm()}

            <div className="md-text-center" style={{ marginTop: '32px' }}>
              <FormattedMessage id="signup.allreadyAccountQuestion" /><br />
              <Link to="/login"><FormattedMessage id="signup.connectHere" /></Link>
            </div>

          </div>
        </div>
      </div>
    );
  }
});



// WEBPACK FOOTER //
// ./src/components/Login/Forgot.js